// ---------------------------------------------------------------------------
// «حسابات التوصيل» - the cashier's quick collection panel, inside the POS.
//
// TWO VIEWS, ONE COLUMN. The till's order column is either the Current Order
// (the ONLY place any order is built) or this panel - a read-only list of the
// deliveries whose money is physically OUT with a driver, each one carrying
// «استلام الحساب». Switching between them is a view switch and nothing else:
// no request, no print, no cart change.
//
// WHAT COUNTS AS "MONEY OUTSIDE". The server answers, not the screen: the
// `awaitingAccount` filter - DELIVERY, unpaid, not cancelled. CREATION IS
// DISPATCH, so every delivery this matches has left the shop by definition;
// the same filter also keeps a legacy row collectible whatever old
// operational status (PREPARING, READY) it was stranded at. Preparation and
// printing are a different concern entirely and neither gate nor describe
// anything here. Outstanding accounts from PREVIOUS days stay on: the badge
// means "accounts not yet back", never "today's deliveries" - that number
// lives on the shift report and is a different fact.
//
// The badge and the list read the SAME query, so they cannot disagree; the
// count is the server's total, not the page in view. Collection opens the
// shared CollectAccountDialog - the canonical payment engine.
//
// WHOSE ACCOUNTS. A delivery's money belongs to the drawer that SENT it out.
// The server scopes the list: a cashier sees only their own; the owner and a
// manager (`orders.collect_others`) see everybody's, can narrow it to one
// cashier, and receive with a button that names whose drawer it joins.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { Banknote, ClipboardList, ShoppingCart, Truck } from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nProvider';
import { useOrders } from '../../api/customers.api';
import { useCurrentUser, usePermission } from '../../hooks/usePermissions';
import { EmptyState, ErrorState, SkeletonRows } from '../../components/common/StateBlocks';
import { formatDateTime, formatLbp } from '../../utils/format';
import type { CustomerOrder } from '../../types';

export type PosView = 'ORDER' | 'ACCOUNTS';

/// The one filter both the badge and the panel read. A single shared object
/// keeps the two on one React Query cache entry - the badge can never say a
/// number the list does not show.
const ACCOUNTS_OUT = { awaitingAccount: true } as const;

/// Whose drawer a delivery's money joins: the shift that sent it out, or -
/// for a delivery sent before deliveries carried a shift - whoever made it.
function senderOf(order: CustomerOrder): { id: string; fullName: string } | null {
  return order.shift?.cashier ?? order.createdBy ?? null;
}

/// How many delivery accounts are still out. Null while unknown - and null
/// on a malformed response, because a badge that guesses is worse than none.
export function usePendingAccountsCount(enabled: boolean): number | null {
  const { data } = useOrders(ACCOUNTS_OUT, enabled);
  return typeof data?.pagination?.total === 'number' ? data.pagination.total : null;
}

// ---------------------------------------------------------------------------
// The switch at the top of the order column
// ---------------------------------------------------------------------------

export function PosViewToggle({
  view,
  onChange,
  pendingCount,
}: {
  view: PosView;
  onChange: (next: PosView) => void;
  /// The authoritative outstanding-accounts figure, for the badge. Null hides
  /// the badge (unknown); zero hides it too - a noisy ⓪ answers nothing.
  pendingCount: number | null;
}) {
  const { t } = useI18n();

  return (
    <div
      role="radiogroup"
      aria-label={t.delivery.posViewLabel}
      className="grid shrink-0 grid-cols-2 gap-1.5"
    >
      {(
        [
          { value: 'ORDER' as const, label: t.delivery.currentOrderTab, Icon: ShoppingCart },
          { value: 'ACCOUNTS' as const, label: t.delivery.accountsTab, Icon: Truck },
        ]
      ).map(({ value, label, Icon }) => {
        const selected = view === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(value)}
            className={clsx(
              'flex h-11 items-center justify-center gap-2 rounded-control border text-sm font-semibold transition-colors',
              selected
                ? 'border-butcher-green bg-butcher-green text-white'
                : 'border-line-strong bg-surface text-ink hover:bg-surface-sunken',
            )}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{label}</span>
            {value === 'ACCOUNTS' && pendingCount !== null && pendingCount > 0 ? (
              <span
                className={clsx(
                  'numeric inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold',
                  selected ? 'bg-white text-butcher-green' : 'bg-burgundy-red text-white',
                )}
              >
                {pendingCount}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The panel itself
// ---------------------------------------------------------------------------

export function DeliveryAccountsPanel({
  flow,
  onCollect,
  onPreview,
}: {
  /// Same contract as CartPanel: 'fill' owns a column and scrolls inside
  /// itself; 'natural' sits in the drawer, which scrolls for it.
  flow: 'fill' | 'natural';
  /// Opens the SHARED CollectAccountDialog - owned by the page, so the full
  /// panel cannot grow a separate collection path.
  onCollect: (order: CustomerOrder) => void;
  /// Opens the READ-ONLY receipt preview - the canonical receipt modal on
  /// the order's frozen snapshot, so the cashier can verify WHICH account
  /// they are about to receive. Looking is free: no write, no print, no
  /// payment - those stay behind their own explicit buttons.
  onPreview: (order: CustomerOrder) => void;
}) {
  const { t, locale, format } = useI18n();
  const canCollect = usePermission('sales.create');
  const seesEveryone = usePermission('orders.collect_others');
  const me = useCurrentUser();

  const [page, setPage] = useState(1);
  const [cashierId, setCashierId] = useState('');
  // The unfiltered first page doubles as the badge's query (same cache
  // entry) and as the source of the cashier choices.
  const { data: everyone } = useOrders(ACCOUNTS_OUT, seesEveryone);
  const filters = cashierId ? { ...ACCOUNTS_OUT, cashierId } : ACCOUNTS_OUT;
  const { data, isLoading, isPlaceholderData, error, refetch } = useOrders(page === 1 ? filters : { ...filters, page });
  const totalPages = Math.max(1, data?.pagination?.totalPages ?? 1);
  useEffect(() => { if (!isPlaceholderData && page > totalPages) setPage(totalPages); }, [page, totalPages, isPlaceholderData]);
  const orders = data?.orders ?? [];
  const total = data?.pagination?.total ?? orders.length;

  const senders = useMemo(() => {
    const byId = new Map<string, string>();
    for (const order of everyone?.orders ?? []) {
      const sender = senderOf(order);
      if (sender) byId.set(sender.id, sender.fullName);
    }
    return [...byId.entries()].map(([id, fullName]) => ({ id, fullName })).sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [everyone]);

  return (
    <section
      className={clsx(
        'flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-card',
        flow === 'fill' && 'h-full min-h-0',
      )}
      aria-label={t.delivery.accountsTab}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Truck className="h-4 w-4 text-butcher-green" aria-hidden="true" />
          {t.delivery.accountsTab}
        </h2>

        {total > 0 ? (
          <span className="numeric rounded-full bg-burgundy-red/10 px-2.5 py-0.5 text-xs font-semibold text-burgundy-red">
            {format(t.delivery.pendingCountLine, { count: total })}
          </span>
        ) : null}
      </header>

      {seesEveryone ? (
        <div className="shrink-0 border-b border-line px-4 py-2.5">
          <select
            aria-label={t.shift.allCashiers}
            className="input h-10 w-full"
            value={cashierId}
            onChange={(event) => {
              setCashierId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{format(t.delivery.accountsAllCashiers, { count: everyone?.pagination?.total ?? 0 })}</option>
            {senders.map((sender) => (
              <option key={sender.id} value={sender.id}>
                {sender.fullName}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink-muted">{t.delivery.accountsScopeHint}</p>
        </div>
      ) : null}

      {/* The list scrolls by itself in a column; in the drawer it grows and
          the drawer scrolls - one scrollbar either way. */}
      <div
        className={clsx(
          'min-w-0',
          flow === 'fill' ? 'scrollbar-brand min-h-0 flex-1 overflow-y-auto' : 'shrink-0',
        )}
      >
        {isLoading ? (
          <div className="p-4">
            <SkeletonRows rows={3} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : orders.length === 0 ? (
          <EmptyState
            title={t.delivery.noneAwaiting}
            description={t.delivery.noneAwaitingHint}
            icon={<ClipboardList className="h-6 w-6" />}
          />
        ) : (
          <ul className="divide-y divide-line">
            {orders.map((order) => {
              const sender = senderOf(order);
              // A cashier's list is theirs alone (the server scopes it), so only
              // the owner or a manager ever receives into somebody else's drawer.
              const othersDrawer = seesEveryone && sender && me && sender.id !== me.id ? sender : null;
              return (
              <li key={order.id} className="min-w-0 space-y-1.5 px-4 py-3">
                {/* THE CARD BODY IS THE PREVIEW. One tap shows the frozen
                    receipt so the cashier can check WHICH account the money
                    in their hand belongs to. It is its own button, a sibling
                    of «استلام الحساب» below - never its parent - so looking
                    can never bubble into paying. */}
                <button
                  type="button"
                  onClick={() => onPreview(order)}
                  aria-label={`${t.orders.viewReceipt} ${order.orderNumber}`}
                  className="block w-full space-y-1.5 rounded-control text-start transition-colors hover:bg-surface-soft"
                >
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="numeric text-sm font-semibold text-ink" dir="ltr">
                      {order.orderNumber}
                    </span>
                    {/* The FROZEN total the receipt printed - the figure the
                        driver owes, never today's re-pricing. */}
                    <span className="numeric text-sm font-bold text-butcher-green">
                      {order.totalLbp ? formatLbp(order.totalLbp, locale) : ''}
                    </span>
                  </span>

                  {/* The RECEIPT identity, beside the order reference above -
                      the YB number on the paper in the driver's hand, which
                      the completed sale will keep. Reconciliation matches the
                      physical receipt to this line. Legacy rows with no
                      reserved number say nothing rather than repeating OR. */}
                  {order.receiptNumber ? (
                    <span className="block text-xs text-ink-muted">
                      {t.delivery.receiptRef}{' '}
                      <span className="numeric" dir="ltr">
                        {order.receiptNumber}
                      </span>
                    </span>
                  ) : null}

                  {/* «توصيل» plainly - never the kitchen's operational word.
                      This panel answers "whose money is outside?", and telling
                      the cashier «قيد التحضير» here (as a stranded legacy row
                      would) answers a question nobody asked at the till. */}
                  <span className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-ink-muted">
                    <span className="inline-flex items-center gap-1">
                      <Truck className="h-3 w-3" aria-hidden="true" />
                      {t.payment.fulfilmentDelivery}
                    </span>
                    <span>
                      {order.items.length === 1
                        ? t.pos.oneItem
                        : format(t.pos.items, { count: order.items.length })}
                    </span>
                  </span>

                  <span className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs">
                    <span className="text-ink-muted">
                      {/* WHEN it went out - stamped at creation now. A legacy
                          row that never recorded a departure shows its
                          creation time plainly, without the «خرج» claim. */}
                      {order.outForDeliveryAt
                        ? `${t.delivery.leftAt} ${formatDateTime(order.outForDeliveryAt, locale)}`
                        : formatDateTime(order.createdAt, locale)}
                    </span>
                    <span className="font-medium text-burgundy-red">
                      {t.delivery.awaitingAccount}
                    </span>
                  </span>

                  {seesEveryone && sender ? (
                    <span className="block text-xs font-medium text-ink">
                      {sender.fullName}
                      {order.shift ? (
                        <span className="numeric ms-1 text-ink-muted" dir="ltr">
                          {order.shift.shiftNumber}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </button>

                {order.deliveryFeePending || order.totalLbp === null || order.exchangeRateUsdToLbp === null ? (
                  <Link to={`/pos?orderId=${order.id}`} className="btn-secondary h-auto min-h-10 w-full">
                    {order.deliveryFeePending ? t.menu.orders.feePending : t.menu.orders.openInPos}
                  </Link>
                ) : canCollect ? (
                  <button
                    type="button"
                    onClick={() => onCollect(order)}
                    className="btn-primary h-10 w-full"
                  >
                    <Banknote className="h-4 w-4" aria-hidden="true" />
                    {othersDrawer ? format(t.delivery.receiveFor, { name: othersDrawer.fullName }) : t.delivery.collectAccount}
                  </button>
                ) : null}
              </li>
              );
            })}
          </ul>
        )}
      </div>

      {totalPages > 1 ? (
        <footer className="flex items-center justify-between gap-2 border-t border-line p-3">
          <button type="button" className="btn-secondary" disabled={page <= 1 || isPlaceholderData} onClick={() => setPage(value => value - 1)}>{t.common.back}</button>
          <span className="numeric text-sm" aria-live="polite">{page} / {totalPages}</span>
          <button type="button" className="btn-secondary" disabled={page >= totalPages || isPlaceholderData} onClick={() => setPage(value => value + 1)} aria-label={`${page + 1} / ${totalPages}`}>›</button>
        </footer>
      ) : null}
    </section>
  );
}
